import type { ExecutedStep } from "../api/types.js";
import { SPEND_PERIODS, type ReadAsk, type SpendPeriod, type SpendRange } from "../adapters/reads.js";

/**
 * DCTA-203 (Claude's rule, 13 Oct) — when the bank drafts a READ, the app shows the bank's answer for exactly what the bank
 * executed. An executed read answers with WHICH bank read answers it (`executed[].read`, DCTA-77 ask z: never a figure)
 * and what it named beside it — a spend's period and category, DCTA-202's `card_id`, the account a balance names (asked of
 * the bank). This maps that answer to the read the app makes, and nothing else: no default of the app's own (a card
 * details read that names no card is every card the read lists), and a read this app has no card for stays the bank's
 * answer (`other`) — never "can't do that yet". A protective action's result line is not a read.
 */
export function bankReadsOf(executed: readonly ExecutedStep[] | undefined): ReadAsk[] {
  return (executed ?? []).flatMap((e) => {
    const r = bankReadOf(e);
    return r === undefined ? [] : [r];
  });
}

const isPeriod = (v: unknown): v is SpendPeriod => typeof v === "string" && (SPEND_PERIODS as readonly string[]).includes(v);
const text = (v: unknown): string | undefined => (typeof v === "string" && v !== "" ? v : undefined);
const isDay = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

/**
 * DCTA-206 (A1): WHEN a spend read reads — the period it names, or its dates (`from`/`to`, never beside `period`); none, the
 * read's own default. `undefined` when the read names a period the app cannot make exactly (an unknown key, one date, a
 * period beside dates): the bank's answer is then drawn without a card, never this month's figures under its line.
 */
function spendWhen(query: Readonly<Record<string, string>> | undefined): { period?: SpendPeriod; range?: SpendRange } | undefined {
  const { period, from, to } = query ?? {};
  if (period !== undefined) return isPeriod(period) && from === undefined && to === undefined ? { period } : undefined;
  if (from === undefined && to === undefined) return {};
  return isDay(from) && isDay(to) ? { range: { from, to } } : undefined;
}

/** DCTA-206 (A2): WHAT it reads — several categories (`categories`), one (`category`) or all; `undefined`, a list it cannot use. */
function spendWhat(e: ExecutedStep): { category?: string; categories?: readonly string[] } | undefined {
  const list: unknown = e.categories;
  if (list === undefined) {
    const category = text(e.category);
    return category === undefined ? {} : { category };
  }
  return Array.isArray(list) && list.length > 0 && list.every((c) => text(c) !== undefined) ? { categories: list as readonly string[] } : undefined;
}

function bankReadOf(e: ExecutedStep): ReadAsk | undefined {
  const path: unknown = e.read?.path;
  if (typeof path !== "string") return undefined;
  // DCTA-204 (R3, R4): the bank's line for the screen and the kind that speaks it — always said as given.
  const line = text(e.answer_line);
  const speech = text(e.speech_read);
  const bank = { ...(line === undefined ? {} : { line }), ...(speech === undefined ? {} : { speech }) };
  switch (path) {
    case "/v1/accounts": {
      const accountId = text(e.account_id);
      return { kind: "balance", ...(accountId === undefined ? {} : { accountId }), bank };
    }
    case "/v1/spending": {
      const when = spendWhen(e.read?.query);
      const what = spendWhat(e);
      return when === undefined || what === undefined ? otherRead(e, path, bank) : { kind: "spend", ...when, ...what, bank };
    }
    case "/v1/cards": {
      const cardId = text(e.card_id);
      return cardId === undefined ? { kind: "card_details", every: true, bank } : { kind: "card_details", cardId, bank };
    }
    case "/v1/bills":
      return { kind: "bills", bank };
    case "/v1/limits":
      return { kind: "limits", bank };
    case "/v1/ledger/entries": {
      // DCTA-204 (R1): the read is made exactly as named — one account's lines when its query names one.
      const accountId = text(e.read?.query?.["account_id"]) ?? text(e.account_id);
      return { kind: "recent", ...(accountId === undefined ? {} : { accountId }), bank };
    }
    case "/v1/gateway/pending":
      return { kind: "pending", bank };
    case "/v1/mandates": {
      // DCTA-209 (F61, spec §13.7): the read is made exactly as named — one destination's schedules when its query names one.
      const destination = text(e.read?.query?.["destination_id"]);
      return { kind: "scheduled", ...(destination === undefined ? {} : { destination }), ...(bank === undefined ? {} : { bank }) };
    }
    default:
      return otherRead(e, path, bank);
  }
}

/** A read this app has no card for: the bank's answer, with its way on (DCTA-204 R7). */
function otherRead(e: ExecutedStep, path: string, bank: ReadAsk["bank"]): ReadAsk {
  const ways = (e.next_actions ?? []).filter((w) => typeof w.label === "string" && w.action !== undefined);
  return { kind: "other", path, ...(ways.length === 0 ? {} : { ways }), ...(bank === undefined ? {} : { bank }) };
}
